import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChartPie, 
  faChartBar, 
  faUsers, 
  faListAlt, 
  faTicketAlt,
  faEllipsisV,
  faUser,
  faUserCheck,
  faUserSlash,
  faFlag,
  faSearch,
  faChevronDown,
  faChevronUp,
  faEnvelope,
  faPhone,
  faCalendarAlt,
  faMapMarkerAlt,
  faGraduationCap,
  faIdCard,
  faTools
} from '@fortawesome/free-solid-svg-icons';

//
// --- helpers (added) ---
async function safeJson(res) {
  const text = await res.text();
  try { return JSON.parse(text); }
  catch {
    console.error("Non-JSON/invalid JSON from API:", text);
    throw new Error("Server did not return valid JSON");
  }
}

function normalizeMember(m = {}) {
  return {
    id: String(m.SLIITLeoID ?? m.id ?? '').trim(),
    name: m.FullName ?? m.name ?? '',
    regNo: String(m.SLIITID ?? m.regNo ?? '').trim(),
    clubId: String(m.SLIITLeoID ?? m.clubId ?? '').trim(),
    faculty: m.Faculty ?? '',
    year: m.Year ?? '',
    semester: m.Semester ?? '',
    status: m.LeoClubStatus ?? m.status ?? 'Active',
    myLCI: Number(m.MyLCI ?? 0) === 1 ? 1 : 0,
    role: m.Role ?? 'Club Member',
    registered: m.RegDate ?? '',
    email: m.Email ?? '',
    contact: m.ContactNo ?? '',
    dob: m.DOB ?? '',
    address: m.Address ?? '',
    postal: m.PostalCode ?? '',
    school: m.School ?? '',
    skills: m.Skills ?? '',
    nic: m.NIC ?? '',
    gender: m.Gender ?? '',
    AccountBlock: Number(m.AccountBlock ?? 0),
    image: m.Image ?? ''
  };
}
// --- end helpers ---

function MemberDatabase() {
  // State management
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [dropdownMember, setDropdownMember] = useState(null);
  const [viewingMember, setViewingMember] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null); // kept as-is

  // Notification system
  const showNotification = (message, type = 'info') => {
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg text-white ${
      type === 'success' ? 'bg-green-500' : 
      type === 'error' ? 'bg-red-500' : 
      type === 'warning' ? 'bg-yellow-500' : 
      'bg-blue-500'
    }`;
    notification.textContent = message;
    
    // Add to DOM
    document.body.appendChild(notification);
    
    // Auto remove after 5 seconds
    setTimeout(() => {
      notification.style.opacity = '0';
      notification.style.transition = 'opacity 0.5s ease';
      
      // Remove from DOM after fade out
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 500);
    }, 5000);
  };
  
  // Open member profile modal
  const openMemberProfile = (member) => {
    setViewingMember(member);
    setIsModalOpen(true);
  };
  
  // Close member profile modal
  const closeModal = () => {
    setIsModalOpen(false);
    setViewingMember(null);
  };

  // Get user info from localStorage
  const user = JSON.parse(localStorage.getItem('user')) || {
    name: 'Unknown User',
    role: 'Member',
    image: '/user.png',
  };

  // Fetch members data (replaced with robust version)
  useEffect(() => {
    const fetchMembers = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await fetch('https://dev.sliitleo.org/Backend/member_api.php', {
          headers: { 'Accept': 'application/json' }
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const raw = await safeJson(response);

        const rows =
          Array.isArray(raw) ? raw :
          Array.isArray(raw.data) ? raw.data :
          Array.isArray(raw.members) ? raw.members :
          null;

        if (!rows) {
          console.error("Unexpected payload shape:", raw);
          throw new Error("Unexpected response payload from server");
        }

        setMembers(rows.map(normalizeMember));
      } catch (err) {
        console.error('Error fetching members:', err);
        setError('Failed to load members. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchMembers();
  }, []);

  // Helper to calculate age from DOB string (YYYY-MM-DD or similar)
  const calculateAge = (dob) => {
    if (!dob) return '';
    const dobDate = new Date(dob);
    if (isNaN(dobDate)) return '';
    const today = new Date();
    let age = today.getFullYear() - dobDate.getFullYear();
    const m = today.getMonth() - dobDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dobDate.getDate())) {
      age--;
    }
    return age;
  };

  // Filter members based on search (made safe)
  const filteredMembers = members.filter(member => {
    const s = search.toLowerCase();
    return (
      (member.name || '').toLowerCase().includes(s) ||
      String(member.regNo || '').toLowerCase().includes(s) ||
      String(member.clubId || '').toLowerCase().includes(s)
    );
  });

  // Pagination logic
  const pageCount = Math.ceil(filteredMembers.length / rowsPerPage);
  const paginatedMembers = filteredMembers.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // Toggle member selection
  const toggleMember = (SLIITLeoID) => {
    setSelectedMember(selectedMember === SLIITLeoID ? null : SLIITLeoID);
    setDropdownMember(null); // Close dropdown when opening details
  };

  // Toggle MYLCI status for a member (use normalized id)
  const toggleMyLCI = async (SLIITLeoID, currentStatus) => {
    try {
      const newStatus = currentStatus ? 0 : 1; // Toggle between 0 and 1
      
      // Optimistic UI update
      setMembers(members => members.map(member => 
        member.id === SLIITLeoID 
          ? { ...member, myLCI: newStatus, updating: true } 
          : member
      ));

      // Update in database
      const response = await fetch('https://dev.sliitleo.org/Backend/member_api.php', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          SLIITLeoID: SLIITLeoID,
          MyLCI: newStatus
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update MYLCI status');
      }

      // Update local state with response data
      setMembers(members => members.map(member => 
        member.id === SLIITLeoID 
          ? { ...member, updating: false }
          : member
      ));
      
      // Close dropdown
      setDropdownMember(null);
      
    } catch (error) {
      console.error('Error updating MYLCI status:', error);
      // Revert optimistic update on error
      setMembers(members => members.map(member => 
        member.id === SLIITLeoID 
          ? { ...member, myLCI: currentStatus, updating: false }
          : member
      ));
      alert('Failed to update MYLCI status. Please try again.');
    }
  };

  // Toggle dropdown
  const toggleDropdown = (memberId, e) => {
    e.stopPropagation();
    setDropdownMember(dropdownMember === memberId ? null : memberId);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex bg-[#111827] text-white min-h-screen">
        <Sidebar />
        <Topbar user={user} />
        <main className="md:ml-64 ml-0 mt-14 w-full px-2 sm:px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-10">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        </main>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex bg-[#111827] text-white min-h-screen">
        <Sidebar />
        <Topbar user={user} />
        <main className="md:ml-64 ml-0 mt-14 w-full px-2 sm:px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-10">
          <div className="bg-red-900/50 border border-red-700 rounded-lg p-4 text-center">
            <p className="text-red-200">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-2 px-4 py-2 bg-red-700 hover:bg-red-600 rounded-md transition-colors"
            >
              Retry
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex bg-[#111827] text-white min-h-screen">
      <Sidebar />
      <Topbar user={user} />
      
      <main className="md:ml-64 ml-0 mt-14 w-full px-2 sm:px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-10">
        {/* Header */}
        <div className="backdrop-blur-md bg-gradient-to-r from-[#23272f]/80 to-[#181b20]/80 border border-gray-800 rounded-2xl p-3 sm:p-4 md:p-5 mb-6 sm:mb-8 flex items-center gap-3 sm:gap-4 shadow-lg">
          <span className="bg-red-600 p-2 sm:p-3 rounded-full shadow-lg flex-shrink-0">
            <FontAwesomeIcon icon={faUsers} className="text-white text-lg" />
          </span>
          <div className="min-w-0">
            <h1 className="text-base sm:text-lg font-bold tracking-wide text-white truncate">Member Database</h1>
            <p className="text-sm sm:text-base text-gray-400 truncate">Manage and view all members</p>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-3 mt-4">
            <Link to="/club-statistics" className="flex items-center gap-2 bg-[#23272f] px-6 py-2 rounded-xl text-base font-semibold text-white border border-gray-700 shadow hover:scale-105 transition focus:outline-none focus:ring-2 focus:ring-orange-400 group">
              <span className="text-gray-400 text-lg"><FontAwesomeIcon icon={faChartPie} /></span>
              Club Statistics
            </Link>
            <Link to="/reporting-hub" className="flex items-center gap-2 bg-[#23272f] px-6 py-2 rounded-xl text-base font-semibold text-white border border-gray-700 shadow hover:scale-105 transition focus:outline-none focus:ring-2 focus:ring-blue-400 group">
              <span className="text-blue-400 text-lg"><FontAwesomeIcon icon={faChartBar} /></span>
              Reporting Hub
            </Link>
            <Link to="/member-database" className="flex items-center gap-2 bg-blue-900/50 px-6 py-2 rounded-xl text-base font-semibold text-white border border-blue-700 shadow hover:scale-105 transition focus:outline-none focus:ring-2 focus:ring-red-400 group">
              <span className="text-red-400 text-lg"><FontAwesomeIcon icon={faUsers} /></span>
              Member Database
            </Link>
            <Link to="/category-selection" className="flex items-center gap-2 bg-[#23272f] px-6 py-2 rounded-xl text-base font-semibold text-white border border-gray-700 shadow hover:scale-105 transition focus:outline-none focus:ring-2 focus:ring-green-400 group">
              <span className="text-green-400 text-lg"><FontAwesomeIcon icon={faListAlt} /></span>
              Category Selections
            </Link>
            <Link to="/tickets-admin" className="flex items-center gap-2 bg-[#23272f] px-6 py-2 rounded-xl text-base font-semibold text-white border border-gray-700 shadow hover:scale-105 transition focus:outline-none focus:ring-2 focus:ring-pink-400 group">
              <span className="text-pink-400 text-lg"><FontAwesomeIcon icon={faTicketAlt} /></span>
              Tickets Admin
            </Link>
          </div>
        </div>

        {/* Search Bar */}
        <div className="bg-gray-800 rounded-2xl p-3 sm:p-4 mb-6 flex items-center gap-3 border border-gray-800">
          <span className="text-gray-400 text-lg">
            <FontAwesomeIcon icon={faSearch} />
          </span>
          <input
            type="text"
            placeholder="Search members by name, ID, or club ID..."
            className="w-full bg-transparent outline-none text-white placeholder-gray-500 text-sm sm:text-base"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Mobile View */}
        <div className="md:hidden space-y-4">
          {paginatedMembers.length > 0 ? (
            paginatedMembers.map((member) => (
              <div
                key={member.id}
                className={`bg-gradient-to-br from-[#23272f]/80 to-[#181b20]/80 rounded-2xl p-4 border ${member.flagged ? 'border-red-500/50' : 'border-gray-800'} shadow-lg transition-all duration-200 ${
                  selectedMember === member.id ? 'ring-2 ring-blue-500' : ''
                }`}
                onClick={() => toggleMember(member.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="bg-gray-800 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg">
                      {(member.name?.charAt(0) || '?').toUpperCase()}
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{member.name}</h3>
                      <p className="text-xs text-gray-400">{member.regNo}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      member.myLCI ? 'bg-green-900/50 text-green-300' : 'bg-gray-700/50 text-gray-300'
                    }`}>
                      {member.myLCI ? 'MYLCI' : 'Non-MYLCI'}
                    </span>
                    <button
                      className="text-gray-400 hover:text-white p-1"
                      onClick={(e) => toggleDropdown(member.id, e)}
                    >
                      <FontAwesomeIcon icon={faEllipsisV} />
                    </button>
                  </div>
                </div>

                {dropdownMember === member.id && (
                  <div className="mt-3 pt-3 border-t border-gray-800">
                    <div className="flex space-x-2">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleMember(member.id);
                        }}
                        className="flex-1 flex items-center justify-center gap-2 bg-blue-900/50 hover:bg-blue-800 text-blue-200 text-xs py-2 px-3 rounded-lg transition-colors"
                      >
                        <FontAwesomeIcon icon={faUser} className="text-xs" />
                        View
                      </button>
                      <button className="flex-1 flex items-center justify-center gap-2 bg-green-900/50 hover:bg-green-800 text-green-200 text-xs py-2 px-3 rounded-lg transition-colors">
                        <FontAwesomeIcon icon={faEnvelope} className="text-xs" />
                        Message
                      </button>
                      <button className="flex-1 flex items-center justify-center gap-2 bg-purple-900/50 hover:bg-purple-800 text-purple-200 text-xs py-2 px-3 rounded-lg transition-colors">
                        <FontAwesomeIcon icon={faTools} className="text-xs" />
                        Actions
                      </button>
                    </div>
                  </div>
                )}

                {selectedMember === member.id && (
                  <div className="mt-4 pt-4 border-t border-gray-800">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="flex items-start space-x-2">
                        <span className="text-gray-500 mt-0.5">
                          <FontAwesomeIcon icon={faEnvelope} className="text-xs" />
                        </span>
                        <div>
                          <p className="text-gray-400 text-xs">Email</p>
                          <p className="text-white">{member.email || 'N/A'}</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-2">
                        <span className="text-gray-500 mt-0.5">
                          <FontAwesomeIcon icon={faPhone} className="text-xs" />
                        </span>
                        <div>
                          <p className="text-gray-400 text-xs">Contact</p>
                          <p className="text-white">{member.contact || 'N/A'}</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-2">
                        <span className="text-gray-500 mt-0.5">
                          <FontAwesomeIcon icon={faGraduationCap} className="text-xs" />
                        </span>
                        <div>
                          <p className="text-gray-400 text-xs">Faculty</p>
                          <p className="text-white">{member.faculty || 'N/A'}</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-2">
                        <span className="text-gray-500 mt-0.5">
                          <FontAwesomeIcon icon={faIdCard} className="text-xs" />
                        </span>
                        <div>
                          <p className="text-gray-400 text-xs">Role</p>
                          <p className="text-white">{member.role || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-400">
              <p>No members found matching your search.</p>
            </div>
          )}
        </div>

        {/* Desktop View */}
        <div className="hidden md:block overflow-x-auto rounded-2xl shadow-lg border border-gray-800 bg-gradient-to-br from-[#23272f]/80 to-[#181b20]/80 backdrop-blur-md">
          <table className="w-full divide-y divide-gray-700 text-sm">
            <thead className="bg-[#23272f]/90 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Member</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Faculty</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Role</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {paginatedMembers.length > 0 ? (
                paginatedMembers.map((member) => (
                  <React.Fragment key={member.id}>
                    <tr 
                      className={`hover:bg-gray-800/50 cursor-pointer transition-colors ${
                        selectedMember === member.id ? 'bg-gray-800/70' : ''
                      }`}
                      onClick={() => toggleMember(member.id)}
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-gray-800 rounded-full flex items-center justify-center text-white font-bold">
                            {(member.name?.charAt(0) || '?').toUpperCase()}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-white" title={member.name}>
                              {member.name.length > 30 ? `${member.name.substring(0, 25)}...` : member.name}
                            </div>
                            <div className="text-xs text-gray-400">{member.email || 'No email'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-300">{member.regNo}</div>
                        <div className="text-xs text-gray-500">LCS{member.clubId}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          member.status === 'Active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {member.status || 'Inactive'}
                        </span>
                        <div className="mt-1 text-xs text-gray-400">
                          {member.myLCI ? 'MYLCI Member' : 'Non-MYLCI'}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                        {member.faculty || 'N/A'}
                        <div className="text-xs text-gray-500">
                          {member.year || '?'} S{member.semester || '?'}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                        {member.role || 'Member'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          {/* <button
                            className="text-blue-400 hover:text-blue-300 p-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              // View action
                            }}
                          >
                            <FontAwesomeIcon icon={faUser} />
                          </button> */}
                          <button
                            className="text-green-400 hover:text-green-300 p-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (member.email) {
                                window.location.href = `mailto:${member.email}?subject=Message from Leo Club Portal`;
                              } else {
                                showNotification('No email address available for this member', 'warning');
                              }
                            }}
                            title="Send email to this member"
                          >
                            <FontAwesomeIcon icon={faEnvelope} />
                          </button>
                          <div className="relative">
                            <button
                              className="text-gray-400 hover:text-white p-1"
                              onClick={(e) => toggleDropdown(member.id, e)}
                            >
                              <FontAwesomeIcon icon={faEllipsisV} />
                            </button>
                            {dropdownMember === member.id && (
                              <div className="absolute right-0 mt-2 w-48 bg-gray-800 rounded-md shadow-lg py-1 z-20">
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleMyLCI(member.id, member.myLCI);
                                  }}
                                  disabled={member.updating}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <FontAwesomeIcon 
                                    icon={member.myLCI ? faUserSlash : faUserCheck} 
                                    className={member.myLCI ? "text-yellow-400" : "text-green-400"} 
                                  />
                                  {member.myLCI ? 'Remove from MYLCI' : 'Add to MYLCI'}
                                </button>
                                <button 
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    const loadingButton = e.currentTarget;
                                    const originalText = loadingButton.innerHTML;
                                    
                                    try {
                                      // Show loading state
                                      loadingButton.disabled = true;
                                      loadingButton.innerHTML = `
                                        <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        ${member.AccountBlock ? 'Unrestricting...' : 'Restricting...'}
                                      `;

                                      const response = await fetch('https://dev.sliitleo.org/Backend/toggle_restrict_login.php', {
                                        method: 'POST',
                                        headers: {
                                          'Content-Type': 'application/json',
                                          'Accept': 'application/json'
                                        },
                                        body: JSON.stringify({
                                          sliitleo_id: member.id,                  // fixed: use normalized id
                                          current_status: member.AccountBlock || 0
                                        })
                                      });
                                      
                                      const result = await response.json();
                                      
                                      if (!response.ok) {
                                        throw new Error(result.message || 'Server error');
                                      }

                                      if (result.success) {
                                        // Update the member's AccountBlock status in the local state
                                        const updatedMembers = members.map(m => 
                                          m.id === member.id 
                                            ? { ...m, AccountBlock: result.new_status } 
                                            : m
                                        );
                                        setMembers(updatedMembers);
                                        
                                        // Show success toast/notification
                                        showNotification(
                                          `User login ${result.new_status ? 'restricted' : 'unrestricted'} successfully`,
                                          result.new_status ? 'success' : 'info'
                                        );
                                      } else {
                                        throw new Error(result.message || 'Failed to update login status');
                                      }
                                    } catch (error) {
                                      console.error('Error toggling login restriction:', error);
                                      showNotification(
                                        error.message || 'An error occurred while updating login status',
                                        'error'
                                      );
                                    } finally {
                                      // Reset button state
                                      if (loadingButton) {
                                        loadingButton.disabled = false;
                                        loadingButton.innerHTML = originalText;
                                      }
                                      // close the same dropdown state used everywhere else
                                      setDropdownMember(null);
                                    }
                                  }}
                                  className={`w-full text-left px-4 py-2 text-sm ${member.AccountBlock ? 'text-green-300' : 'text-yellow-400'} hover:bg-gray-700 flex items-center gap-2`}
                                >
                                  <FontAwesomeIcon 
                                    icon={member.AccountBlock ? faUserCheck : faUserSlash} 
                                    className={member.AccountBlock ? 'text-green-400' : 'text-yellow-400'} 
                                  />
                                  {member.AccountBlock ? 'Unrestrict Login' : 'Restrict Login'}
                                </button>
                                <button className="w-full text-left px-4 py-2 text-sm text-red-300 hover:bg-gray-700 flex items-center gap-2">
                                  <FontAwesomeIcon icon={faFlag} />
                                  Flag Member
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                    {selectedMember === member.id && (
                      <tr className="bg-gray-900/50">
                        <td colSpan="6" className="px-4 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <div>
                              <h4 className="text-sm font-medium text-gray-400 mb-2">Contact Information</h4>
                              <div className="space-y-2">
                                <div className="flex items-start">
                                  <FontAwesomeIcon icon={faEnvelope} className="text-gray-500 mt-0.5 mr-2 flex-shrink-0" />
                                  <div>
                                    <p className="text-xs text-gray-400">Email</p>
                                    <p className="text-sm text-white">{member.email || 'N/A'}</p>
                                  </div>
                                </div>
                                <div className="flex items-start">
                                  <FontAwesomeIcon icon={faPhone} className="text-gray-500 mt-0.5 mr-2 flex-shrink-0" />
                                  <div>
                                    <p className="text-xs text-gray-400">Phone</p>
                                    <p className="text-sm text-white">{member.contact || 'N/A'}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div>
                              <h4 className="text-sm font-medium text-gray-400 mb-2">Personal Details</h4>
                              <div className="space-y-2">
                                <div className="flex items-start">
                                  <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-500 mt-0.5 mr-2 flex-shrink-0" />
                                  <div>
                                    <p className="text-xs text-gray-400">Date of Birth</p>
                                    <p className="text-sm text-white">
                                      {member.dob ? `${formatDate(member.dob)} (${calculateAge(member.dob)} years)` : 'N/A'}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-start">
                                  <FontAwesomeIcon icon={faMapMarkerAlt} className="text-gray-500 mt-0.5 mr-2 flex-shrink-0" />
                                  <div>
                                    <p className="text-xs text-gray-400">Address</p>
                                    <p className="text-sm text-white">
                                      {member.address ? `${member.address}, ${member.postal}` : 'N/A'}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-start">
                                  <FontAwesomeIcon icon={faUser} className="text-gray-500 mt-0.5 mr-2 flex-shrink-0" />
                                  <div>
                                    <p className="text-xs text-gray-400">NIC</p>
                                    <p className="text-sm text-white">
                                      {member.nic ? `${member.nic}` : 'N/A'}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div>
                              <h4 className="text-sm font-medium text-gray-400 mb-2">Education</h4>
                              <div className="space-y-2">
                                <div className="flex items-start">
                                  <FontAwesomeIcon icon={faGraduationCap} className="text-gray-500 mt-0.5 mr-2 flex-shrink-0" />
                                  <div>
                                    <p className="text-xs text-gray-400">School</p>
                                    <p className="text-sm text-white">{member.school || 'N/A'}</p>
                                  </div>
                                </div>
                                <div className="flex items-start">
                                  <FontAwesomeIcon icon={faTools} className="text-gray-500 mt-0.5 mr-2 flex-shrink-0" />
                                  <div>
                                    <p className="text-xs text-gray-400">Skills</p>
                                    <p className="text-sm text-white">{member.skills || 'N/A'}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="mt-4 pt-4 border-t border-gray-800">
                            <div className="flex items-center justify-between">
                              <div className="text-xs text-gray-500">
                                Member since: {member.registered ? formatDate(member.registered) : 'N/A'}
                              </div>
                              <div className="flex space-x-2">
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openMemberProfile(member);
                                  }}
                                  className="px-3 py-1.5 text-xs font-medium rounded-md bg-blue-900/50 text-blue-200 hover:bg-blue-800/50 transition-colors"
                                >
                                  View Full Profile
                                </button>
                                <a 
                                  href={`https://wa.me/+94${member.contact ? member.contact.replace(/\D/g, '') : ''}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="px-3 py-1.5 text-xs font-medium rounded-md bg-green-900/50 text-green-200 hover:bg-green-800/50 transition-colors block text-center"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  WhatsApp
                                </a>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-400">
                    No members found matching your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pageCount > 1 && (
          <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-sm text-gray-400">
              Showing {Math.min((page - 1) * rowsPerPage + 1, filteredMembers.length)}-{
                Math.min(page * rowsPerPage, filteredMembers.length)
              } of {filteredMembers.length} members
            </div>
            <div className="flex items-center gap-2">
              <button
                className="px-3 sm:px-4 py-1.5 sm:py-2 rounded bg-gray-700 text-white text-sm sm:text-base disabled:opacity-40 disabled:cursor-not-allowed"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                &larr; Previous
              </button>
              <span className="text-xs sm:text-sm text-gray-300 whitespace-nowrap">
                Page {page} of {pageCount}
              </span>
              <button
                className="px-3 sm:px-4 py-1.5 sm:py-2 rounded bg-gray-700 text-white text-sm sm:text-base disabled:opacity-40 disabled:cursor-not-allowed"
                onClick={() => setPage(p => Math.min(pageCount, p + 1))}
                disabled={page === pageCount}
              >
                Next &rarr;
              </button>
            </div>
          </div>
        )}
        {/* Member Profile Modal */}
        {isModalOpen && viewingMember && (
      <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-700 shadow-2xl shadow-black/50">
          {/* Modal Header */}
          <div className="p-6 border-b border-gray-700 flex justify-between items-center">
            <h3 className="text-xl font-semibold text-white">Member Profile</h3>
            <button 
              onClick={closeModal}
              className="text-gray-400 hover:text-white"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Modal Body */}
          <div className="p-6">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Left Column - Profile */}
              <div className="md:w-1/3">
                <div className="bg-gray-800 rounded-lg p-4 text-center">
                  <div className="w-24 h-24 mx-auto rounded-full bg-gray-700 mb-4 overflow-hidden">
                    {viewingMember.image ? (
                      <img 
                        src={viewingMember.image} 
                        alt={viewingMember.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-600 text-2xl text-gray-300">
                        {(viewingMember.name?.charAt(0) || '?').toUpperCase()}
                      </div>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-white">{viewingMember.name}</h3>
                  <p className="text-sm text-gray-400">{viewingMember.role || 'Member'}</p>
                  
                  <div className="mt-4 pt-4 border-t border-gray-700">
                    <div className="flex items-center justify-center space-x-2">
                      <span className={`px-3 py-1 text-xs rounded-full ${
                        viewingMember.myLCI ? 'bg-green-900/50 text-green-300' : 'bg-gray-700/50 text-gray-300'
                      }`}>
                        {viewingMember.myLCI ? 'MYLCI Member' : 'Non-MYLCI'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      Member since: {viewingMember.registered ? formatDate(viewingMember.registered) : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Right Column - Details */}
              <div className="md:w-2/3">
                <h4 className="text-lg font-medium text-white mb-4">Personal Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-800/50 p-4 rounded-lg">
                    <h5 className="text-sm font-medium text-gray-400 mb-2 flex items-center">
                      <FontAwesomeIcon icon={faIdCard} className="mr-2" />
                      ID & Contact
                    </h5>
                    <div className="space-y-2">
                      <p className="text-sm">
                        <span className="text-gray-400">ID:</span> {viewingMember.regNo || 'N/A'}
                      </p>
                      <p className="text-sm">
                        <span className="text-gray-400">Club ID:</span> LCS{viewingMember.clubId || 'N/A'}
                      </p>
                      <p className="text-sm">
                        <span className="text-gray-400">Email:</span> {viewingMember.email || 'N/A'}
                      </p>
                      <p className="text-sm">
                        <span className="text-gray-400">Phone:</span> {viewingMember.contact || 'N/A'}
                      </p>
                      <p className="text-sm">
                          <span className="text-gray-400">Birth Date:</span> {viewingMember.dob || 'N/A'}
                        </p>
                    </div>
                  </div>
                  
                  <div className="bg-gray-800/50 p-4 rounded-lg">
                    <h5 className="text-sm font-medium text-gray-400 mb-2 flex items-center">
                      <FontAwesomeIcon icon={faGraduationCap} className="mr-2" />
                      Education
                    </h5>
                    <div className="space-y-2">
                      <p className="text-sm">
                        <span className="text-gray-400">Faculty:</span> {viewingMember.faculty || 'N/A'}
                      </p>
                      <p className="text-sm">
                        <span className="text-gray-400">Year:</span> {viewingMember.year || 'N/A'} S{viewingMember.semester || 'N/A'}
                      </p>
                      <p className="text-sm">
                          <span className="text-gray-400">School:</span> {viewingMember.school || 'N/A'}
                        </p>
                        <p className="text-sm">
                          <span className="text-gray-400">Skills:</span> {viewingMember.skills || 'N/A'}
                        </p>
                    </div>
                  </div>
                  
                  <div className="bg-gray-800/50 p-4 rounded-lg md:col-span-2">
                    <h5 className="text-sm font-medium text-gray-400 mb-2 flex items-center">
                      <FontAwesomeIcon icon={faUser} className="mr-2" />
                      Additional Information
                    </h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm">
                          <span className="text-gray-400">Position:</span> {viewingMember.position || 'Member'}
                        </p>
                        <p className="text-sm">
                          <span className="text-gray-400">Status:</span> {viewingMember.status || 'Active'}
                        </p>
                        <p className="text-sm">
                          <span className="text-gray-400">Address:</span> {viewingMember.address || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm">
                          <span className="text-gray-400">Age:</span> {viewingMember.dob ? calculateAge(viewingMember.dob) : 'N/A'}
                        </p>
                        <p className="text-sm">
                          <span className="text-gray-400">Gender:</span> {viewingMember.gender || 'N/A'}
                        </p>
                        
                        
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-700 flex justify-end space-x-3">
              <button 
                onClick={closeModal}
                className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700/50 hover:bg-gray-600/50 rounded-md transition-colors"
              >
                Close
              </button>
              <a 
                href={`https://wa.me/+94${viewingMember.contact ? viewingMember.contact.replace(/\D/g, '') : ''}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors flex items-center justify-center gap-2"
              >
                <FontAwesomeIcon icon={faPhone} className="text-xs" />
                Message on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    )}
      </main>
    </div>
  );
}

export default MemberDatabase;
